import { useState, useEffect } from 'react';
import { clsx } from 'clsx';
import { useTenant } from '../contexts/TenantContext';
import EcommerceSettings from '../components/settings/EcommerceSettings';
import HardwareSettings from '../components/settings/HardwareSettings';
import POSSettings from '../components/settings/POSSettings';
import EnfactSettings from '../components/settings/EnfactSettings';
import Team from './Team';
import { api } from '../services/api';
import { logger } from '../utils/logger';

type Tab = 'general' | 'ecommerce' | 'payments' | 'hardware' | 'pos' | 'features' | 'integrations' | 'security' | 'team';

interface TabConfig {
  id: Tab;
  name: string;
  icon: string;
  description: string;
}

const tabs: TabConfig[] = [
  {
    id: 'general',
    name: 'Général',
    icon: 'ℹ️',
    description: 'Informations de base de votre boutique',
  },
  {
    id: 'ecommerce',
    name: 'E-Commerce',
    icon: '🛍️',
    description: 'Apparence et configuration de la boutique en ligne',
  },
  {
    id: 'payments',
    name: 'Paiements & Taxes',
    icon: '💳',
    description: 'Modes de paiement et taux de TVA',
  },
  {
    id: 'hardware',
    name: 'Hardware',
    icon: '🖨️',
    description: 'Balance, imprimante, scanner',
  },
  {
    id: 'pos',
    name: 'Point de Vente',
    icon: '💳',
    description: 'Configuration POS et auto-déconnexion',
  },
  {
    id: 'features',
    name: 'Fonctionnalités',
    icon: '🎯',
    description: 'Activer/désactiver les fonctionnalités',
  },
  {
    id: 'integrations',
    name: 'Intégrations',
    icon: '🔗',
    description: 'Enfact, comptabilité, API externes',
  },
  {
    id: 'security',
    name: 'Sécurité',
    icon: '🔒',
    description: 'PIN, permissions, logs',
  },
  {
    id: 'team',
    name: 'Équipe',
    icon: '👥',
    description: 'Utilisateurs et rôles',
  },
];

export default function Settings() {
  const { tenant } = useTenant();
  const [activeTab, setActiveTab] = useState<Tab>('general');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Paramètres</h1>
          <p className="mt-1 text-sm text-gray-500">
            Configuration de votre boutique {tenant?.name}
          </p>
          <p className="mt-2 text-xs text-amber-600">
            💡 Chaque section a son propre bouton de sauvegarde en bas
          </p>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={clsx(
                  'flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 whitespace-nowrap transition-colors',
                  activeTab === tab.id
                    ? 'border-emerald-500 text-emerald-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                )}
              >
                <span className="text-xl">{tab.icon}</span>
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'general' && <GeneralTab tenant={tenant} />}
          {activeTab === 'ecommerce' && <EcommerceTab />}
          {activeTab === 'payments' && <PaymentsTab tenant={tenant} />}
          {activeTab === 'hardware' && <HardwareTab key={tenant?.updated_at || tenant?.id} />}
          {activeTab === 'pos' && <POSSettings />}
          {activeTab === 'features' && <FeaturesTab tenant={tenant} />}
          {activeTab === 'integrations' && <IntegrationsTab />}
          {activeTab === 'security' && <SecurityTab />}
          {activeTab === 'team' && <TeamTab />}
        </div>
      </div>
    </div>
  );
}

// ===============================================
// TAB COMPONENTS
// ===============================================

function GeneralTab({ tenant }: { tenant: any }) {
  const { reloadTenant } = useTenant();
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(tenant?.config?.general?.logo || null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // États pour les champs du formulaire
  const [formData, setFormData] = useState({
    name: tenant?.name || '',
    slug: tenant?.slug || '',
    email: tenant?.config?.general?.email || '',
    phone: tenant?.config?.general?.phone || '',
    address: tenant?.config?.general?.address || '',
    postalCode: tenant?.config?.general?.postalCode || '',
    city: tenant?.config?.general?.city || '',
    description: tenant?.config?.general?.description || '',
    language: tenant?.config?.general?.language || 'fr',
    currency: tenant?.config?.general?.currency || 'EUR',
    timezone: tenant?.config?.general?.timezone || 'Europe/Paris',
  });

  // Mettre à jour les données quand le tenant change
  useEffect(() => {
    setFormData({
      name: tenant?.name || '',
      slug: tenant?.slug || '',
      email: tenant?.config?.general?.email || '',
      phone: tenant?.config?.general?.phone || '',
      address: tenant?.config?.general?.address || '',
      postalCode: tenant?.config?.general?.postalCode || '',
      city: tenant?.config?.general?.city || '',
      description: tenant?.config?.general?.description || '',
      language: tenant?.config?.general?.language || 'fr',
      currency: tenant?.config?.general?.currency || 'EUR',
      timezone: tenant?.config?.general?.timezone || 'Europe/Paris',
    });
    setLogoPreview(tenant?.config?.general?.logo || null);
  }, [tenant]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Vérifier la taille (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setSaveMessage({ type: 'error', text: 'Le fichier est trop volumineux (max 2MB)' });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    // Vérifier le type
    if (!file.type.startsWith('image/')) {
      setSaveMessage({ type: 'error', text: 'Le fichier doit être une image (PNG, JPG, SVG)' });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    setLogoFile(file);

    // Créer un aperçu
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };

  const handleSave = async () => {
    if (!tenant?.id) {
      setSaveMessage({ type: 'error', text: 'Erreur: Aucun tenant sélectionné' });
      return;
    }

    setSaving(true);
    setSaveMessage(null);

    try {
      // Uploader le logo si un nouveau fichier a été sélectionné
      let logoUrl = logoPreview;
      if (logoFile) {
        setUploadingLogo(true);
        // Convertir le fichier en base64 pour le stocker dans la config
        // Dans une vraie app production, vous utiliseriez un service de stockage (S3, Cloudinary, etc.)
        const reader = new FileReader();
        logoUrl = await new Promise<string>((resolve) => {
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(logoFile);
        });
        setUploadingLogo(false);
      }

      const config = {
        general: {
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          postalCode: formData.postalCode,
          city: formData.city,
          description: formData.description,
          language: formData.language,
          currency: formData.currency,
          timezone: formData.timezone,
          logo: logoUrl,
        },
      };

      // Mise à jour du tenant via l'API
      await api.updateTenantConfig(tenant.id, config);

      // Si le nom ou slug a changé, mettre à jour également ces champs
      if (formData.name !== tenant.name || formData.slug !== tenant.slug) {
        // TODO: Ajouter une méthode API pour mettre à jour nom et slug
        logger.log('[GeneralTab] Nom/Slug changé - TODO: implémenter updateTenant');
      }

      setSaveMessage({ type: 'success', text: 'Configuration sauvegardée avec succès !' });
      setLogoFile(null); // Reset le fichier après sauvegarde

      // Recharger le tenant
      await reloadTenant();

      // Effacer le message après 3 secondes
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      logger.error('[GeneralTab] Erreur sauvegarde:', error);
      setSaveMessage({
        type: 'error',
        text: `Erreur lors de la sauvegarde: ${error instanceof Error ? error.message : 'Erreur inconnue'}`,
      });
    } finally {
      setSaving(false);
      setUploadingLogo(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Informations générales
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Nom de la boutique */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nom de la boutique *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="Les Folies Maraîchères"
            />
          </div>

          {/* Slug / URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              URL de la boutique *
            </label>
            <div className="flex">
              <span className="inline-flex items-center px-3 border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm rounded-l-lg">
                folies.app/
              </span>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => handleChange('slug', e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="ma-boutique"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              L'URL ne peut contenir que des lettres, chiffres et tirets
            </p>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email de contact
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="contact@maboutique.com"
            />
          </div>

          {/* Téléphone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Téléphone
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="+33 1 23 45 67 89"
            />
          </div>

          {/* Adresse */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Adresse physique
            </label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => handleChange('address', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent mb-2"
              placeholder="123 Rue du Commerce"
            />
            <div className="grid grid-cols-2 gap-4">
              <input
                type="text"
                value={formData.postalCode}
                onChange={(e) => handleChange('postalCode', e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="Code postal"
              />
              <input
                type="text"
                value={formData.city}
                onChange={(e) => handleChange('city', e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="Ville"
              />
            </div>
          </div>

          {/* Description */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              rows={4}
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="Décrivez votre boutique et vos produits..."
            />
          </div>

          {/* Logo */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Logo de la boutique
            </label>
            <div className="flex items-start gap-4">
              {/* Aperçu du logo */}
              <div className="relative">
                {logoPreview ? (
                  <div className="w-24 h-24 rounded-lg border-2 border-gray-300 overflow-hidden bg-white">
                    <img
                      src={logoPreview}
                      alt="Logo"
                      className="w-full h-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="w-24 h-24 rounded-lg bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                )}
                {logoPreview && (
                  <button
                    type="button"
                    onClick={handleRemoveLogo}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full hover:bg-red-600 flex items-center justify-center text-xs font-bold"
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* Boutons d'action */}
              <div className="flex-1">
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                  onChange={handleLogoChange}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => document.getElementById('logo-upload')?.click()}
                  disabled={uploadingLogo}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploadingLogo ? '⏳ Chargement...' : logoPreview ? 'Changer l\'image' : 'Choisir une image'}
                </button>
                <p className="mt-2 text-xs text-gray-500">
                  PNG, JPG ou SVG. Max 2 MB. Recommandé : 500x500px
                </p>
                {logoFile && (
                  <p className="mt-1 text-xs text-emerald-600 font-medium">
                    ✓ Nouveau fichier sélectionné : {logoFile.name}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <hr />

      {/* Localisation */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Localisation
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Langue
            </label>
            <select
              value={formData.language}
              onChange={(e) => handleChange('language', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="fr">Français</option>
              <option value="en">English</option>
              <option value="nl">Nederlands</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Devise
            </label>
            <select
              value={formData.currency}
              onChange={(e) => handleChange('currency', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="EUR">EUR (€)</option>
              <option value="USD">USD ($)</option>
              <option value="GBP">GBP (£)</option>
              <option value="CHF">CHF (Fr.)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fuseau horaire
            </label>
            <select
              value={formData.timezone}
              onChange={(e) => handleChange('timezone', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="Europe/Paris">Europe/Paris (GMT+1)</option>
              <option value="Europe/Brussels">Europe/Brussels (GMT+1)</option>
              <option value="Europe/London">Europe/London (GMT+0)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Bouton de sauvegarde */}
      <div className="flex items-center justify-end gap-4 pt-6 border-t-2 border-gray-200">
        {saveMessage && (
          <div
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              saveMessage.type === 'success'
                ? 'bg-green-100 text-green-800 border border-green-300'
                : 'bg-red-100 text-red-800 border border-red-300'
            }`}
          >
            {saveMessage.type === 'success' ? '✓ ' : '⚠️ '}
            {saveMessage.text}
          </div>
        )}
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
        >
          {saving ? '⏳ Sauvegarde...' : '💾 Sauvegarder les modifications'}
        </button>
      </div>
    </div>
  );
}

function EcommerceTab() {
  return <EcommerceSettings />;
}

function PaymentsTab({ tenant }: { tenant: any }) {
  const { reloadTenant } = useTenant();
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [editingMethod, setEditingMethod] = useState<string | null>(null);
  const [editingMethodName, setEditingMethodName] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newMethodName, setNewMethodName] = useState('');
  const [newMethodIcon, setNewMethodIcon] = useState('💳');

  // Liste des moyens de paiement par défaut (non supprimables)
  // const defaultMethods = ['cash', 'card', 'wallet', 'check', 'transfer']; // Unused - kept for reference

  // Initialiser avec les méthodes par défaut + les méthodes personnalisées du tenant
  const initializePaymentMethods = (tenantConfig: any) => {
    const defaults = {
      cash: {
        enabled: true,
        name: 'Espèces',
        icon: '💵',
        isDefault: true,
      },
      card: {
        enabled: true,
        name: 'Carte bancaire',
        icon: '💳',
        isDefault: true,
      },
      wallet: {
        enabled: true,
        name: 'Portefeuille prépayé',
        icon: '👛',
        isDefault: true,
      },
      check: {
        enabled: true,
        name: 'Chèque',
        icon: '🏦',
        isDefault: true,
      },
      transfer: {
        enabled: false,
        name: 'Virement bancaire',
        icon: '🔀',
        isDefault: true,
      },
    };

    const tenantMethods = tenantConfig?.payments?.methods || {};
    const result: { [key: string]: { enabled: boolean; name: string; icon: string; isDefault?: boolean } } = {};

    // Fusionner les méthodes par défaut avec celles du tenant
    Object.keys(defaults).forEach((key) => {
      result[key] = {
        ...defaults[key as keyof typeof defaults],
        ...tenantMethods[key],
        isDefault: true,
      };
    });

    // Ajouter les méthodes personnalisées du tenant
    Object.keys(tenantMethods).forEach((key) => {
      if (!defaults[key as keyof typeof defaults]) {
        result[key] = {
          ...tenantMethods[key],
          isDefault: false,
        };
      }
    });

    return result;
  };

  // États pour les méthodes de paiement avec possibilité d'édition
  const [paymentMethods, setPaymentMethods] = useState<{
    [key: string]: { enabled: boolean; name: string; icon: string; isDefault?: boolean };
  }>(initializePaymentMethods(tenant?.config));

  // États pour les taux de TVA
  const [taxRates, setTaxRates] = useState({
    standard: tenant?.config?.payments?.taxRates?.standard ?? 21,
    reduced: tenant?.config?.payments?.taxRates?.reduced ?? 12,
    food: tenant?.config?.payments?.taxRates?.food ?? 6,
    zero: tenant?.config?.payments?.taxRates?.zero ?? 0,
  });

  useEffect(() => {
    setPaymentMethods(initializePaymentMethods(tenant?.config));
    setTaxRates({
      standard: tenant?.config?.payments?.taxRates?.standard ?? 21,
      reduced: tenant?.config?.payments?.taxRates?.reduced ?? 12,
      food: tenant?.config?.payments?.taxRates?.food ?? 6,
      zero: tenant?.config?.payments?.taxRates?.zero ?? 0,
    });
  }, [tenant]);

  const handlePaymentMethodToggle = (methodId: string, enabled: boolean) => {
    setPaymentMethods((prev) => ({
      ...prev,
      [methodId]: { ...prev[methodId], enabled },
    }));
  };

  const handleEditMethod = (methodId: string) => {
    setEditingMethod(methodId);
    setEditingMethodName(paymentMethods[methodId].name);
  };

  const handleSaveMethodName = (methodId: string) => {
    if (editingMethodName.trim()) {
      setPaymentMethods((prev) => ({
        ...prev,
        [methodId]: { ...prev[methodId], name: editingMethodName.trim() },
      }));
    }
    setEditingMethod(null);
    setEditingMethodName('');
  };

  const handleCancelEdit = () => {
    setEditingMethod(null);
    setEditingMethodName('');
  };

  const handleAddCustomMethod = () => {
    if (!newMethodName.trim()) {
      setSaveMessage({ type: 'error', text: 'Le nom du moyen de paiement est obligatoire' });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    // Générer un ID unique basé sur le nom
    const methodId = 'custom_' + newMethodName.toLowerCase().replace(/[^a-z0-9]/g, '_');

    // Vérifier si l'ID existe déjà
    if (paymentMethods[methodId]) {
      setSaveMessage({ type: 'error', text: 'Ce moyen de paiement existe déjà' });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    setPaymentMethods((prev) => ({
      ...prev,
      [methodId]: {
        enabled: true,
        name: newMethodName.trim(),
        icon: newMethodIcon,
        isDefault: false,
      },
    }));

    setNewMethodName('');
    setNewMethodIcon('💳');
    setShowAddModal(false);
  };

  const handleDeleteCustomMethod = (methodId: string) => {
    if (paymentMethods[methodId]?.isDefault) {
      setSaveMessage({ type: 'error', text: 'Impossible de supprimer un moyen de paiement par défaut' });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    if (confirm(`Êtes-vous sûr de vouloir supprimer "${paymentMethods[methodId].name}" ?`)) {
      const newMethods = { ...paymentMethods };
      delete newMethods[methodId];
      setPaymentMethods(newMethods);
    }
  };

  const handleTaxRateChange = (type: string, value: number) => {
    setTaxRates((prev) => ({ ...prev, [type]: value }));
  };

  // Liste d'icônes suggérées pour les moyens de paiement
  const suggestedIcons = ['💳', '💵', '🏦', '🎫', '🎁', '🌱', '♻️', '🍃', '🎟️', '🔖', '💰', '🪙'];

  const handleSave = async () => {
    if (!tenant?.id) {
      setSaveMessage({ type: 'error', text: 'Erreur: Aucun tenant sélectionné' });
      return;
    }

    setSaving(true);
    setSaveMessage(null);

    try {
      const config = {
        payments: {
          methods: paymentMethods,
          taxRates: taxRates,
        },
      };

      await api.updateTenantConfig(tenant.id, config);
      setSaveMessage({ type: 'success', text: 'Configuration sauvegardée avec succès !' });
      await reloadTenant();
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      logger.error('[PaymentsTab] Erreur sauvegarde:', error);
      setSaveMessage({
        type: 'error',
        text: `Erreur lors de la sauvegarde: ${error instanceof Error ? error.message : 'Erreur inconnue'}`,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Modes de paiement acceptés
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Activez ou désactivez les moyens de paiement et personnalisez leurs noms
            </p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium transition-colors"
          >
            + Ajouter un moyen
          </button>
        </div>

        {/* Modal d'ajout */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Ajouter un moyen de paiement
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom du moyen de paiement *
                  </label>
                  <input
                    type="text"
                    value={newMethodName}
                    onChange={(e) => setNewMethodName(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="Ex: Chèques Repas Pluxee"
                    autoFocus
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Exemples : Chèques Repas Pluxee, Écochèques Edenred, Sodexo Pass Restaurant
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Icône
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {suggestedIcons.map((icon) => (
                      <button
                        key={icon}
                        type="button"
                        onClick={() => setNewMethodIcon(icon)}
                        className={`w-12 h-12 text-2xl border-2 rounded-lg hover:border-emerald-500 transition-colors ${
                          newMethodIcon === icon ? 'border-emerald-500 bg-emerald-50' : 'border-gray-300'
                        }`}
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                  <input
                    type="text"
                    value={newMethodIcon}
                    onChange={(e) => setNewMethodIcon(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="Ou entrez un emoji"
                    maxLength={2}
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setNewMethodName('');
                    setNewMethodIcon('💳');
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
                >
                  Annuler
                </button>
                <button
                  onClick={handleAddCustomMethod}
                  className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium"
                >
                  Ajouter
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {Object.entries(paymentMethods).map(([methodId, method]) => (
            <div
              key={methodId}
              className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <input
                type="checkbox"
                checked={method.enabled}
                onChange={(e) => handlePaymentMethodToggle(methodId, e.target.checked)}
                className="w-5 h-5 text-emerald-600"
              />
              <span className="text-2xl">{method.icon}</span>
              {editingMethod === methodId ? (
                <div className="flex-1 flex items-center gap-2">
                  <input
                    type="text"
                    value={editingMethodName}
                    onChange={(e) => setEditingMethodName(e.target.value)}
                    className="flex-1 px-3 py-1.5 border border-emerald-500 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="Nom du moyen de paiement"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveMethodName(methodId);
                      if (e.key === 'Escape') handleCancelEdit();
                    }}
                  />
                  <button
                    onClick={() => handleSaveMethodName(methodId)}
                    className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium"
                  >
                    ✓
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm font-medium"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{method.name}</span>
                    {!method.isDefault && (
                      <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                        Personnalisé
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEditMethod(methodId)}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Modifier
                    </button>
                    {!method.isDefault && (
                      <button
                        onClick={() => handleDeleteCustomMethod(methodId)}
                        className="text-sm text-red-600 hover:text-red-800 font-medium"
                      >
                        Supprimer
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800">
            💡 <strong>Astuce :</strong> Ajoutez des moyens de paiement personnalisés comme les chèques repas (Pluxee, Edenred, Sodexo),
            écochèques, ou tout autre moyen spécifique à votre activité.
          </p>
        </div>
      </div>

      <hr />

      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Taux de TVA
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Configurez les taux de TVA applicables dans votre pays
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Taux standard
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={taxRates.standard}
                onChange={(e) => handleTaxRateChange('standard', parseFloat(e.target.value) || 0)}
                step="0.01"
                min="0"
                max="100"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
              />
              <span className="text-gray-600 font-medium">%</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Taux normal (ex: 21% en Belgique, 20% en France)
            </p>
          </div>

          <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Taux réduit
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={taxRates.reduced}
                onChange={(e) => handleTaxRateChange('reduced', parseFloat(e.target.value) || 0)}
                step="0.01"
                min="0"
                max="100"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
              />
              <span className="text-gray-600 font-medium">%</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Taux intermédiaire (ex: 12% en Belgique, 10% en France)
            </p>
          </div>

          <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Taux alimentaire
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={taxRates.food}
                onChange={(e) => handleTaxRateChange('food', parseFloat(e.target.value) || 0)}
                step="0.01"
                min="0"
                max="100"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
              />
              <span className="text-gray-600 font-medium">%</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Produits alimentaires de base (ex: 6% en Belgique, 5.5% en France)
            </p>
          </div>

          <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Taux zéro
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={taxRates.zero}
                onChange={(e) => handleTaxRateChange('zero', parseFloat(e.target.value) || 0)}
                step="0.01"
                min="0"
                max="100"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
              />
              <span className="text-gray-600 font-medium">%</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Produits exonérés de TVA
            </p>
          </div>
        </div>

        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex gap-3">
            <span className="text-blue-600 text-xl">ℹ️</span>
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900">
                Information importante
              </p>
              <p className="text-xs text-blue-800 mt-1">
                Les taux de TVA sont définis par votre pays. Assurez-vous d'utiliser les taux en vigueur pour votre activité.
                Ces taux seront utilisés automatiquement lors de la création de produits et des ventes.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bouton de sauvegarde */}
      <div className="flex items-center justify-end gap-4 pt-6 border-t-2 border-gray-200">
        {saveMessage && (
          <div
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              saveMessage.type === 'success'
                ? 'bg-green-100 text-green-800 border border-green-300'
                : 'bg-red-100 text-red-800 border border-red-300'
            }`}
          >
            {saveMessage.type === 'success' ? '✓ ' : '⚠️ '}
            {saveMessage.text}
          </div>
        )}
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
        >
          {saving ? '⏳ Sauvegarde...' : '💾 Sauvegarder les modifications'}
        </button>
      </div>
    </div>
  );
}

function HardwareTab() {
  return <HardwareSettings />;
}

function FeaturesTab({ tenant }: { tenant: any }) {
  const { reloadTenant } = useTenant();
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [features, setFeatures] = useState({
    consignes: tenant?.config?.features?.consignes ?? true,
    wallet: tenant?.config?.features?.wallet ?? true,
    loyalty: tenant?.config?.features?.loyalty ?? false,
    inventory: tenant?.config?.features?.inventory ?? true,
    multi_vendor: tenant?.config?.features?.multi_vendor ?? true,
    live_shopping: tenant?.config?.features?.live_shopping ?? false,
  });

  useEffect(() => {
    setFeatures({
      consignes: tenant?.config?.features?.consignes ?? true,
      wallet: tenant?.config?.features?.wallet ?? true,
      loyalty: tenant?.config?.features?.loyalty ?? false,
      inventory: tenant?.config?.features?.inventory ?? true,
      multi_vendor: tenant?.config?.features?.multi_vendor ?? true,
      live_shopping: tenant?.config?.features?.live_shopping ?? false,
    });
  }, [tenant]);

  const handleFeatureChange = (featureId: string, enabled: boolean) => {
    setFeatures((prev) => ({ ...prev, [featureId]: enabled }));
  };

  const handleSave = async () => {
    if (!tenant?.id) {
      setSaveMessage({ type: 'error', text: 'Erreur: Aucun tenant sélectionné' });
      return;
    }

    setSaving(true);
    setSaveMessage(null);

    try {
      const config = {
        features: features,
      };

      await api.updateTenantConfig(tenant.id, config);
      setSaveMessage({ type: 'success', text: 'Configuration sauvegardée avec succès !' });
      await reloadTenant();
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      logger.error('[FeaturesTab] Erreur sauvegarde:', error);
      setSaveMessage({
        type: 'error',
        text: `Erreur lors de la sauvegarde: ${error instanceof Error ? error.message : 'Erreur inconnue'}`,
      });
    } finally {
      setSaving(false);
    }
  };

  const featuresList = [
    {
      id: 'consignes',
      name: 'Consignes',
      description: 'Gestion des consignes (bouteilles, bocaux)',
    },
    {
      id: 'wallet',
      name: 'Portefeuille prépayé',
      description: 'Permettre aux clients de recharger un wallet',
    },
    {
      id: 'loyalty',
      name: 'Programme fidélité',
      description: 'Points de fidélité et récompenses',
    },
    {
      id: 'inventory',
      name: 'Inventaire avancé',
      description: 'Suivi détaillé des stocks et mouvements',
    },
    {
      id: 'multi_vendor',
      name: 'Multi-vendeurs',
      description: 'Plusieurs vendeurs sur la même boutique',
    },
    {
      id: 'live_shopping',
      name: 'Live Shopping',
      description: 'Ventes en direct avec vidéo temps réel',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Fonctionnalités avancées
        </h2>
        <div className="space-y-3">
          {featuresList.map((feature) => (
            <div
              key={feature.id}
              className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
            >
              <div className="flex-1">
                <p className="font-medium text-gray-900">{feature.name}</p>
                <p className="text-sm text-gray-500">{feature.description}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={features[feature.id as keyof typeof features]}
                  onChange={(e) => handleFeatureChange(feature.id, e.target.checked)}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Bouton de sauvegarde */}
      <div className="flex items-center justify-end gap-4 pt-6 border-t-2 border-gray-200">
        {saveMessage && (
          <div
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              saveMessage.type === 'success'
                ? 'bg-green-100 text-green-800 border border-green-300'
                : 'bg-red-100 text-red-800 border border-red-300'
            }`}
          >
            {saveMessage.type === 'success' ? '✓ ' : '⚠️ '}
            {saveMessage.text}
          </div>
        )}
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
        >
          {saving ? '⏳ Sauvegarde...' : '💾 Sauvegarder les modifications'}
        </button>
      </div>
    </div>
  );
}

function IntegrationsTab() {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-2">
          Intégrations externes
        </h2>
        <p className="text-sm text-gray-500 mb-6">
          Connectez votre boutique à des services externes pour automatiser vos processus.
        </p>
      </div>

      {/* Enfact Settings */}
      <EnfactSettings />

      {/* Placeholder pour futures intégrations */}
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl opacity-50">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center text-2xl">
            📊
          </div>
          <div>
            <h3 className="font-bold text-gray-900">Comptabilité</h3>
            <p className="text-sm text-gray-500">
              Export vers Winbooks, BOB, Exact... (bientôt disponible)
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function SecurityTab() {
  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-500 italic">
        🚧 Configuration sécurité (PIN, permissions, logs) - À implémenter
      </p>
    </div>
  );
}

function TeamTab() {
  return <Team />;
}
